import axios from "axios";

const BASE_URL = process.env.REACT_APP_SOCKET_URL;

// Superadmin API — /api/subscriptions/superadmin/*
const API = axios.create({
  baseURL: `${BASE_URL}/api/subscriptions/superadmin`,
});

// Admin API — /api/subscriptions/admin/*
const adminAPI = axios.create({
  baseURL: `${BASE_URL}/api/subscriptions/admin`,
});

// Driver subscription API — /api/subscriptions/* (for initiate/renew)
const driverSubAPI = axios.create({
  baseURL: `${BASE_URL}/api/subscriptions`,
});

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

// ═══════════════════════════════════════════════════════════════════════════
//  SUPERADMIN ROUTES (/api/subscriptions/superadmin/*)
// ═══════════════════════════════════════════════════════════════════════════

// ─── Driver Subscriptions ────────────────────────────────────────────────────

// 📖 Get All Subscriptions
// GET /api/subscriptions/superadmin/get
export async function getAllSubscriptionsByAdmin(params = {}) {
  try {
    const res = await API.get("/get", {
      headers: authHeader(),
      params, // { status, page, limit }
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}

// 📖 Get Single Driver Subscription Status
// GET /api/subscriptions/superadmin/get/:driverId
export async function getDriverSubscriptionByAdmin(driverId) {
  try {
    const res = await API.get(`/get/${driverId}`, {
      headers: authHeader(),
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}

// ─── Payments ────────────────────────────────────────────────────────────────

// 📖 Get All Payments
// GET /api/subscriptions/superadmin/payments/get
export async function getAllPaymentsByAdmin(params = {}) {
  try {
    const res = await API.get("/payments/get", {
      headers: authHeader(),
      params, // { method, status, page, limit }
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}

// 📊 Get Payment Summary (dashboard totals)
// GET /api/subscriptions/superadmin/payments/summary
export async function getPaymentSummaryByAdmin(params = {}) {
  try {
    const res = await API.get("/payments/summary", {
      headers: authHeader(),
      params, // { from, to }
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}

// 💵 Get Pending Cash Payments
// GET /api/subscriptions/superadmin/payments/pending-cash
export async function getPendingCashPaymentsByAdmin() {
  try {
    const res = await API.get("/payments/pending-cash", {
      headers: authHeader(),
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}

// ✅ Confirm Cash Payment
// POST /api/subscriptions/superadmin/payments/confirm-cash
export async function confirmCashPaymentByAdmin(payload) {
  try {
    // payload: { paymentId, adminId, notes? }
    const res = await API.post("/payments/confirm-cash", payload, {
      headers: authHeader(),
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}

// ─── Driver Payment History ───────────────────────────────────────────────────

// 📖 Get Single Driver Full Payment + Subscription History
// GET /api/subscriptions/superadmin/drivers/:driverId/payments
export async function getDriverPaymentHistoryByAdmin(driverId) {
  try {
    const res = await API.get(`/drivers/${driverId}/payments`, {
      headers: authHeader(),
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}

// ─── Bank Deposit — Admin Routes (/api/subscriptions/admin/*) ────────────────

// 🏦 Get Pending Bank Deposits (cash paid but not yet deposited to bank)
// GET /api/subscriptions/admin/payments/pending-deposits
// Params: { from?, to? } — optional date range filter
export async function getPendingDepositsByAdmin(params = {}) {
  try {
    const cleanParams = {};
    if (params.from) cleanParams.from = params.from;
    if (params.to)   cleanParams.to   = params.to;

    const res = await adminAPI.get("/payments/pending-deposits", {
      headers: authHeader(),
      params: cleanParams,
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}

// 🏦 Mark Cash as Deposited to Bank (Admin action)
// POST /api/subscriptions/admin/payments/mark-deposited
export async function markDepositedToBankByAdmin(payload) {
  try {
    // payload: { paymentId, adminId, receiptNumber?, depositSlipUrl? }
    const res = await adminAPI.post("/payments/mark-deposited", payload, {
      headers: authHeader(),
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.response?.data?.message || err.message };
  }
}

// ─── Bank Deposit — Superadmin Routes (/api/subscriptions/superadmin/*) ──────

// 🔍 Get Unverified Deposits (deposited but not yet verified by superadmin)
// GET /api/subscriptions/superadmin/payments/unverified-deposits
// Params: { from?, to?, page?, limit? }
export async function getUnverifiedDepositsByAdmin(params = {}) {
  try {
    const cleanParams = {};
    if (params.from)  cleanParams.from  = params.from;
    if (params.to)    cleanParams.to    = params.to;
    if (params.page)  cleanParams.page  = params.page;
    if (params.limit) cleanParams.limit = params.limit;

    const res = await API.get("/payments/unverified-deposits", {
      headers: authHeader(),
      params: cleanParams,
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}

// ✅ Verify Bank Deposit (Superadmin confirms deposit is genuine)
// POST /api/subscriptions/superadmin/payments/verify-deposit
export async function verifyBankDepositByAdmin(payload) {
  try {
    // payload: { paymentId, superadminId }
    const res = await API.post("/payments/verify-deposit", payload, {
      headers: authHeader(),
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.response?.data?.message || err.message };
  }
}

// 📖 Get Verified Bank Deposits (list of bank-confirmed records)
// GET /api/subscriptions/superadmin/payments/verified-deposits
// Params: { from?, to?, page?, limit? }
export async function getVerifiedDepositsByAdmin(params = {}) {
  try {
    const cleanParams = {};
    if (params.from)  cleanParams.from  = params.from;
    if (params.to)    cleanParams.to    = params.to;
    if (params.page)  cleanParams.page  = params.page;
    if (params.limit) cleanParams.limit = params.limit;

    const res = await API.get("/payments/verified-deposits", {
      headers: authHeader(),
      params: cleanParams,
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}

// 📥 Export Verified Bank Deposits as Excel (.xlsx download)
// GET /api/subscriptions/superadmin/payments/export-verified-deposits
// Params: { from?, to? } — date range for export
// ✅ Triggers a browser file download
export async function exportVerifiedDepositsByAdmin(params = {}) {
  try {
    const cleanParams = {};
    if (params.from) cleanParams.from = params.from;
    if (params.to)   cleanParams.to   = params.to;

    const res = await API.get("/payments/export-verified-deposits", {
      headers: authHeader(),
      params: cleanParams,
      responseType: "blob",
    });

    // Trigger download in browser
    const blob = new Blob([res.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url  = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href  = url;

    const rangeLabel =
      params.from || params.to
        ? `${params.from || "start"}_to_${params.to || "today"}`
        : "all";
    link.setAttribute("download", `verified-deposits_${rangeLabel}.xlsx`);

    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}

// 📅 Get Daily Collection Report
// GET /api/subscriptions/superadmin/payments/daily-report?date=YYYY-MM-DD
export async function getDailyReportByAdmin(date) {
  try {
    const res = await API.get("/payments/daily-report", {
      headers: authHeader(),
      params: date ? { date } : {},
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}

// ═══════════════════════════════════════════════════════════════════════════
//  DRIVER SUBSCRIPTION ROUTES (/api/subscriptions/*)
//  Using existing backend routes for create/renew
// ═══════════════════════════════════════════════════════════════════════════

// ✅ Create/Initiate Subscription
// POST /api/subscriptions/initiate
export async function createSubscriptionByAdmin(payload) {
  try {
    const res = await driverSubAPI.post("/initiate", payload, {
      headers: authHeader(),
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.response?.data?.message || err.message };
  }
}

// 🔄 Renew Subscription
// POST /api/subscriptions/renew
export async function renewSubscriptionByAdmin(payload) {
  try {
    const res = await driverSubAPI.post("/renew", payload, {
      headers: authHeader(),
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.response?.data?.message || err.message };
  }
}

// 📖 Get Driver Subscription Status (driver route)
// GET /api/subscriptions/status/:driverId
export async function getDriverSubscriptionStatus(driverId) {
  try {
    const res = await driverSubAPI.get(`/status/${driverId}`, {
      headers: authHeader(),
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}

// ✅ Verify Razorpay Payment
// POST /api/subscriptions/verify-payment
export async function verifyPaymentByAdmin(payload) {
  try {
    const res = await driverSubAPI.post("/verify-payment", payload, {
      headers: authHeader(),
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}

// 📍 Get zone-aware plan prices for a specific driver (uses driver's zoneId)
// GET /api/zone-plans/:zoneId  (or falls back to global if no zoneId)
export async function getZonePlanPrices(zoneId) {
  try {
    if (!zoneId) return { success: false };
    const BASE = process.env.REACT_APP_SOCKET_URL;
    const res = await axios.get(`${BASE}/api/zone-plans/${zoneId}`, {
      headers: authHeader(),
    });
    return res.data; // { success, pricing: [{ planName, amount, isOverridden, globalDefault }] }
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}

// POST /api/subscriptions/superadmin/payments/reject-cash
export async function rejectCashPaymentByAdmin(payload) {
  try {
    const res = await API.post("/payments/reject-cash", payload, {
      headers: authHeader(),
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.response?.data?.message || err.message };
  }
}

// 📍 Get Territory-wise Daily Report
// GET /api/subscriptions/admin/payments/territory-report?date=YYYY-MM-DD&zoneId=ZONE123
export async function getTerritoryReportByAdmin({ date, zoneId } = {}) {
  try {
    const params = {};
    if (date)   params.date   = date;
    if (zoneId) params.zoneId = zoneId;
    const res = await adminAPI.get("/payments/territory-report", {
      headers: authHeader(),
      params,
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}